import { useMutation } from '@tanstack/react-query';
import {
  createChat,
  getChat,
  getChatsByOrderId,
  sendMessageToChat,
  updateChat,
} from '@util/firestore/messages';
import { addNewNote, transferSeller } from '@util/firestore/orders';
import { chargeAccount } from '@util/firestore/payments';
import { getUserById } from '@util/firestore/users';
import { logError } from '@util/logError';
import { useToastContext } from 'context/ToastContext';
import { deleteField } from 'firebase/firestore';
import { ChatDocument, ChatInput, ChatMessageDocument } from 'models/chat';
import { OrderDocument, OrderItemDocument } from 'models/order';

export const useUpgrade = ({ onSuccess }: { onSuccess: () => void }) => {
  const { showSuccessToast, showErrorToast } = useToastContext();
  const upgradeMutation = useMutation({
    mutationFn: async ({
      order,
      item,
      sellerUsername,
      seller_id,
      order_id,
      reason,
      chargeSeller,
      chat_id,
      fee,
    }: {
      order: OrderDocument;
      item: OrderItemDocument;
      sellerUsername: string;
      seller_id: string;
      order_id: string;
      reason: string;
      chargeSeller: boolean;
      fee: number;
      chat_id?: string;
    }) => {
      try {
        const seller = await getUserById(seller_id);
        if (!seller) throw new Error('Seller not found');
        await transferSeller(
          order,
          item,
          seller_id,
          sellerUsername,
          'MXLockerDirect'
        );
        showSuccessToast('Order transferred.');

        let chat: ChatDocument | null = null;
        if (chat_id) {
          chat = await getChat(chat_id);
        } else {
          const chats = await getChatsByOrderId(order_id);
          if (chats.length) {
            chat =
              chats.find(
                (c) =>
                  c.seller_id === seller_id &&
                  c.buyer_id === process.env.NEXT_PUBLIC_SUPPORT_ID
              ) || null;
          }
        }
        let content = `MX Locker is now handling this order`;
        if (reason === 'did_not_ship_in_required_time_period') {
          content += ' due to not shipping in time required time period.';
        }
        content +=
          ' The customer has been upgraded, and the order has been removed from your account.';
        const updateMessage: ChatMessageDocument = {
          content,
          content_html: `<p>${content}</p>`,
          created_at: Date.now(),
          is_auto: true,
          is_img: false,
          uid: process.env.NEXT_PUBLIC_SUPPORT_ID!,
          auto_recipient: seller_id,
        };
        if (chat) {
          await sendMessageToChat(updateMessage, chat);
          await updateChat(chat.id, {
            order_id: deleteField(),
          });
          showSuccessToast('Message sent to seller.');
        } else {
          // create a new chat
          const newChat: ChatInput = {
            order_id,
            seller_id,
            buyer_id: process.env.NEXT_PUBLIC_SUPPORT_ID!,
            created_at: Date.now(),
            last_time: Date.now(),
            messages: [updateMessage],
            id: '',
          };
          await createChat(newChat);
          showSuccessToast('Chat created and message sent.');
        }
        if (chargeSeller && fee > 0) {
          try {
            await chargeAccount({
              account_id: seller.account_id,
              amount: fee,
              description: 'Seller Upgrade Fee: ' + reason,
            });
            await addNewNote(
              order_id,
              'Seller (' + sellerUsername + ') charged + ' + fee + ' USD.'
            );
          } catch (e) {
            logError(e);
            showErrorToast('Error charging seller: ' + (e as Error).message);
          }
        }
        return true;
      } catch (e) {
        logError(e);
        showErrorToast('Error upgrading seller: ' + (e as Error).message);
        return false;
      }
    },
    onSuccess,
    onError: (error) => {
      logError(error, undefined, undefined, 'RefundsTable upgrade');
      showErrorToast('Error upgrading seller: ' + error.message);
    },
  });
  return {
    upgradeMutation,
  };
};
